import type { ObjectDirective } from 'vue'

/**
 * Obraz, który zakończył ładowanie BŁĘDEM (404): przeglądarka oznacza go jako
 * `complete`, ale `naturalWidth === 0` (brak pikseli). Pozwala wykryć błąd, który
 * poleciał ZANIM Vue zdążyło podpiąć `@error` — typowe dla SSR: serwer renderuje
 * <img>, przeglądarka pobiera plik i dostaje 404 przed hydracją, więc zdarzenie
 * `error` przepada. Lustrzane do warunku „już załadowany" (`complete && naturalWidth > 0`)
 * używanego niżej w dyrektywie `imgFade`.
 */
export function isBrokenImg(
  img: Pick<HTMLImageElement, 'complete' | 'naturalWidth'>,
): boolean {
  return img.complete && img.naturalWidth === 0
}

/**
 * Dyrektywa `v-img-fade` — łagodne pojawianie się zdjęcia po doczytaniu (koniec „pop-in").
 *
 * SSR-safe: dyrektywa działa wyłącznie po stronie klienta (hook `mounted`),
 * więc SSR renderuje obraz bez klasy → jest widoczny domyślnie. Fade włączamy
 * tylko dla zdjęć, które w momencie montażu NIE są jeszcze załadowane — obrazy
 * z cache/SSR (`complete && naturalWidth > 0`) zostają widoczne bez migotania.
 *
 * Działa na natywnym <img> (NuxtImg renderuje <img>), więc nie zależy od tego,
 * czy komponent przekazuje zdarzenie `@load`.
 *
 * Sterowanie wizualne (opacity/animacja) jest w globalnym CSS (main.css):
 * `img.img-fade` → opacity 0, `img.img-fade--in` → animacja do 1.
 */
export const imgFade: ObjectDirective<HTMLElement> = {
  mounted(el) {
    const img = el instanceof HTMLImageElement ? el : el.querySelector('img')
    if (!img) return

    // Obraz już z cache/SSR — zostaje widoczny, bez migotania „wstecz".
    if (img.complete && img.naturalWidth > 0) return

    img.classList.add('img-fade')

    const reveal = () => {
      img.classList.add('img-fade--in')
      img.removeEventListener('load', reveal)
      img.removeEventListener('error', reveal)
    }

    img.addEventListener('load', reveal)
    // Nie chowaj zepsutych zdjęć (404 → placeholder/alt) — ujawnij mimo błędu.
    img.addEventListener('error', reveal)
  },
}
